import React, { useEffect, useState } from 'react';
import { Modal, Row, Col, Flex, Space, Typography, theme } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';

// Two-step confirmation for migrating a property to Didomi. Step 1 links the
// property with Didomi; step 2 confirms the consent script switch.
function PropertyMigrationModal({ property, flowId, loading, onConfirm, onCancel }) {
  const [step, setStep] = useState(1);
  const { token } = theme.useToken();

  useEffect(() => {
    setStep(1);
  }, [property]);

  const domain = <Typography.Text strong>{property?.domain}</Typography.Text>;

  const handleOk = () => {
    if (step === 1) {
      setStep(2);
    } else {
      onConfirm(property);
    }
  };

  const handleCancel = () => {
    if (step === 2 && !loading) {
      setStep(1);
    } else {
      onCancel();
    }
  };

  return (
    <Modal
      className='didomi-migration-modal'
      width={600}
      open={!!property}
      title="Migrate to Didomi"
      onOk={handleOk}
      okText={step === 1 ? 'Continue' : 'Confirm'}
      okButtonProps={{ loading }}
      cancelText={step === 1 ? 'Cancel' : 'Back'}
      cancelButtonProps={{ disabled: loading }}
      onCancel={handleCancel}
      maskClosable={!loading}
    >
      <Row gutter={[token.marginMD, token.margin]}>
        <Col span={24}>
          <Flex justify="flex-start">
            <ExclamationCircleFilled
              style={{
                fontSize: token.marginLG,
                marginRight: token.margin,
                color: step === 1 ? token.colorWarning : token.colorError,
              }}
            />
            <Space direction="vertical" size={token.marginXS}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {step === 1 ? 'Migrate property' : 'Switch consent script'}
              </Typography.Title>
              {step === 1 ? (
                <Typography.Text type="secondary">
                  Are you sure you want to migrate property {domain} to Didomi? This will
                  link your property with the Didomi consent platform. You'll confirm the
                  script migration in the next step.
                </Typography.Text>
              ) : (
                <Typography.Text type="secondary">
                  Are you sure you want the script you have implemented on this property to
                  start using Didomi? The consent script on {domain} will switch from
                  Sourcepoint to Didomi. Existing consent records will remain, but all future
                  consent collection will be handled by Didomi.
                </Typography.Text>
              )}
            </Space>
          </Flex>
        </Col>
        {flowId && (
          <Col span={24}>
            <Flex
              style={{
                background: token.colorFillTertiary,
                borderRadius: token.borderRadius,
                padding: token.paddingMD,
              }}
              justify="space-between"
              align="center"
            >
              <Typography.Text type="secondary">Linked Didomi flow:</Typography.Text>
              <Typography.Text strong>{flowId}</Typography.Text>
            </Flex>
          </Col>
        )}
      </Row>
    </Modal>
  );
}

export default PropertyMigrationModal;
